<script setup lang="ts">
import AdminUnitsView from './AdminUnitsView.vue'
import {
  AdminUnitsSelection,
  FormBeneficialOwnerPerson,
  FormPerson
} from "@/domains/home/services/nir/forms/components/widgets/types";

const props = defineProps({
  data: {
    type: Array as () => FormBeneficialOwnerPerson[],
  }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: FormBeneficialOwnerPerson[]): void
}>()

function getIdTypeLabel(idType: string): string {
  const labels: Record<string, string> = {
    'national_id': 'National ID',
    'passport': 'Passport',
    'driving_permit': 'Driving Permit',
    'refugee_id': 'Refugee ID',
    'voter_id': 'Voter ID'
  }
  return labels[idType] || idType
}

function formatDate(dateString: string): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
</script>

<template>
  <div class="flex flex-col space-y-4">
    <div v-if="data && data.length > 0">
      <div
        v-for="(owner, index) in data"
        :key="index"
        class="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-blue-50 to-indigo-50 mb-3"
      >
        <div class="flex items-center justify-between mb-4 pb-3 border-b border-blue-200">
          <h4 class="text-base font-semibold text-gray-800">
            <i class="fa-solid fa-user-tie mr-2 text-blue-600"></i>
            Beneficial Owner {{ data.length > 1 ? `#${index + 1}` : '' }}
          </h4>
          <span v-if="owner.idType == 'national_id'" class="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
            <i class="fa-solid fa-flag mr-1"></i>
            Ugandan Citizen
          </span>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <!-- Full Name -->
          <div class="col-span-2">
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Name</label>
            <p class="text-sm text-gray-900 font-medium mt-1">
              {{ owner.givenName }} {{ owner.middleNames }} {{ owner.surname }}
            </p>
          </div>

          <!-- ID Type -->
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">ID Type</label>
            <p class="text-sm text-gray-900 mt-1">
              <i class="fa-solid fa-id-badge text-gray-400 mr-1"></i>
              {{ getIdTypeLabel(owner.idType) }}
            </p>
          </div>

          <!-- ID Number -->
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">ID Number</label>
            <p class="text-sm text-gray-900 font-mono mt-1">{{ owner.idNumber || '-' }}</p>
          </div>

          <!-- Gender -->
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Gender</label>
            <p class="text-sm text-gray-900 mt-1 capitalize">
              <i class="fa-solid fa-venus-mars text-gray-400 mr-1"></i>
              {{ owner.gender || '-' }}
            </p>
          </div>

          <!-- Date of Birth -->
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date of Birth</label>
            <p class="text-sm text-gray-900 mt-1">
              <i class="fa-solid fa-calendar text-gray-400 mr-1"></i>
              {{ formatDate(owner.dateOfBirth) }}
            </p>
          </div>

          <!-- Email -->
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
            <p class="text-sm text-gray-900 mt-1">
              <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
              {{ owner.email || '-' }}
            </p>
          </div>

          <!-- Phone -->
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
            <p class="text-sm text-gray-900 mt-1">
              <i class="fa-solid fa-phone text-gray-400 mr-1"></i>
              {{ owner.phone || '-' }}
            </p>
          </div>

          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Voting Rights Percentage</label>
            <p class="text-sm text-gray-900 mt-1">
              <i class="fa-solid fa-phone text-gray-400 mr-1"></i>
              {{ owner.votingRightsPercentage || '-' }}
            </p>
          </div>

          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ownership Percentage</label>
            <p class="text-sm text-gray-900 mt-1">
              <i class="fa-solid fa-phone text-gray-400 mr-1"></i>
              {{ owner.ownershipPercentage || '-' }}
            </p>
          </div>

          <!-- Address -->
          <div v-if="owner.address" class="col-span-2">
            <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
              <i class="fa-solid fa-map-marker-alt text-gray-400 mr-1"></i>
              Address
            </label>
            <AdminUnitsView :modelValue="owner.address" :data="owner.address" />
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-8 text-gray-500 text-sm">
      <i class="fa fa-user-tie text-3xl mb-2"></i>
      <p>No beneficial owners information available</p>
    </div>
  </div>
</template>

<style scoped></style>
